import { css } from "@emotion/css";
import { ErrorPanel } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { isArgoCDApplicationResource, isPodResource } from "@octopusdeploy/octopus-server-client";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { LiveStatusResourceHealthStatusIcon, LiveStatusResourceSyncStatusIcon } from "~/areas/projects/components/Observability/LiveStatusIcons";
import type { LiveStatusObjectDrawerLoadingTabProps } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/TabProps";
import { commonStyles } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/commonStyles";
import { ArgoCDApplicationSyncDetails } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/components/ArgoCDApplicationSyncDetails";
import { StatusDisplayLookup } from "~/areas/projects/components/Observability/StatusDisplayLookup";
import { createErrorInfo } from "~/areas/tasks/components/Task/Kubernetes/createErrorInfo";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import DateFormatter from "~/utils/DateFormatter/index";
type SummaryTabProps = LiveStatusObjectDrawerLoadingTabProps;
const styles = {
    detailsContainer: css({
        paddingLeft: space[16],
        paddingRight: space[16],
    }),
    statusDD: css({
        display: "flex",
        flexDirection: "row",
        gap: space[8],
    }),
    summaryDetailList: css({
        margin: 0,
        listStyleType: "disc",
        listStylePosition: "inside",
    }),
    loadingDiv: css({
        height: "20px",
    }),
};
export const SummaryTab = (props: SummaryTabProps) => {
    const { detailedResource, isLoading, error } = props;
    const formatArrayDetails = (label: string, values: string[] | ReactNode[] | undefined) => values &&
        values.length > 0 && (<>
                <dt>{label}</dt>
                <dd>
                    <ul className={styles.summaryDetailList}>
                        {values.map((v, i) => (<li key={i}>{v}</li>))}
                    </ul>
                </dd>
            </>);
    const formatRecordDetails = (label: string, values: Record<string, string>, delimiter: string) => {
        const entries = Object.entries(values);
        return (entries.length > 0 && (<>
                    <dt>{label}</dt>
                    <dd>
                        <ul className={styles.summaryDetailList}>
                            {entries.map(([k, v]) => (<li key={k}>
                                    {k}
                                    {delimiter}
                                    {v}
                                </li>))}
                        </ul>
                    </dd>
                </>));
    };
    const customDetails = useMemo(() => {
        if (isPodResource(detailedResource)) {
            return formatArrayDetails("Containers", detailedResource.ManifestSummary?.Containers);
        }
    }, [detailedResource]);
    const detailedResourceErrorInfo = createErrorInfo(error);
    return (<>
            <div className={styles.detailsContainer}>
                {detailedResourceErrorInfo && <ErrorPanel error={detailedResourceErrorInfo}/>}
                {!detailedResourceErrorInfo && (<>
                        <dl className={commonStyles.summaryDetailsList(176, space[32])}>
                            {isLoading &&
                Array.from({ length: 5 }, (_, i) => (<>
                                        <dt>
                                            <div className={styles.loadingDiv}>
                                                <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                                            </div>
                                        </dt>
                                        <dd>
                                            <div className={styles.loadingDiv}>
                                                <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                                            </div>
                                        </dd>
                                    </>))}
                            {!isLoading && detailedResource && (<>
                                    <dt>Health Status</dt>
                                    <dd className={styles.statusDD}>
                                        <LiveStatusResourceHealthStatusIcon status={detailedResource.HealthStatus}/> {detailedResource.HealthStatus}
                                    </dd>
                                    {detailedResource.SyncStatus && (<>
                                            <dt>Sync Status</dt>
                                            <dd className={styles.statusDD}>
                                                <LiveStatusResourceSyncStatusIcon syncStatus={detailedResource.SyncStatus} healthStatus={detailedResource.HealthStatus}/>
                                                {StatusDisplayLookup[detailedResource.SyncStatus]}
                                            </dd>
                                        </>)}
                                    {customDetails}
                                    {detailedResource.ManifestSummary && (<>
                                            {formatRecordDetails("Labels", detailedResource.ManifestSummary.Labels, "=")}
                                            {formatRecordDetails("Annotations", detailedResource.ManifestSummary.Annotations, ": ")}
                                            <dt>Creation Timestamp</dt>
                                            <dd>
                                                <span title={DateFormatter.dateToLongFormat(detailedResource.ManifestSummary.CreationTimestamp) || undefined}>{DateFormatter.dateToShortFormat(detailedResource.ManifestSummary.CreationTimestamp)}</span>
                                            </dd>
                                        </>)}
                                    <dt>Last Updated</dt>
                                    <dd>
                                        <span title={DateFormatter.dateToLongFormat(detailedResource.LastUpdated) || undefined}>{DateFormatter.dateToShortFormat(detailedResource.LastUpdated)}</span>
                                    </dd>
                                </>)}
                        </dl>
                    </>)}
            </div>
            {detailedResource && isArgoCDApplicationResource(detailedResource) && <ArgoCDApplicationSyncDetails syncDetails={detailedResource.ManifestSummary.SourceSyncDetails}/>}
        </>);
};
